import React, { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Контекст авторизации и темы
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Компоненты
import Header from './components/Header';
import Footer from './components/Footer';
import BannedModal from './components/BannedModal';

// Страницы
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import AuthPage from './pages/AuthPage';
import CLIAuthPage from './pages/CLIAuthPage';
import DownloadPage from './pages/DownloadPage';
import SubscribePage from './pages/SubscribePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AdminPage from './pages/AdminPage';
import ErrorPage from './pages/ErrorPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SettingsPage from './pages/SettingsPage';
import RestoreAccountPage from './pages/RestoreAccountPage';

// Типы пропсов
interface AppLayoutProps {
  children: ReactNode;
}

// Компонент для модального окна бана
interface BanInfo {
  banned: boolean;
  reason?: string;
  until?: string | null;
  username?: string;
}

interface AuthContextValue {
  banInfo: BanInfo | null;
  logout: () => void;
  clearBanInfo: () => void;
}

const BanChecker: React.FC = () => {
  const { banInfo, logout, clearBanInfo } = useAuth() as AuthContextValue;

  if (!banInfo) return null;

  return (
    <BannedModal
      banInfo={banInfo}
      onLogout={() => {
        logout();
        clearBanInfo();
      }}
    />
  );
};

// Компонент для адаптивной навигации
const AdaptiveNavigation: React.FC = () => {
  return <Header />;
};

// Компонент для условного рендера Header/Footer
const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register'
    || location.pathname === '/verify-email' || location.pathname === '/forgot-password'
    || location.pathname === '/reset-password';
  const isCLIAuth = location.pathname === '/cli-auth';

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950 relative transition-colors duration-300">
      {/* Модальное окно бана */}
      <BanChecker />

      {/* Адаптивная навигация (скрываем на странице авторизации и CLI auth) */}
      {!isAuthPage && !isCLIAuth && <AdaptiveNavigation />}

      {/* Основной контент */}
      <main className="relative z-10">
        {children}
      </main>

      {/* Подвал (скрываем на странице авторизации) */}
      {!isAuthPage && !isCLIAuth && <Footer />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppLayout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/download" element={<DownloadPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/login" element={<AuthPage mode="login" />} />
              <Route path="/register" element={<AuthPage mode="register" />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/restore-account" element={<RestoreAccountPage />} />
              <Route path="/cli-auth" element={<CLIAuthPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/error/:code" element={<ErrorPage code={404} title="Страница не найдена" message="Запрашиваемая страница не существует или была удалена." />} />
              <Route path="*" element={<ErrorPage code={404} title="Страница не найдена" message="Запрашиваемая страница не существует или была удалена." />} />
            </Routes>
          </AppLayout>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;

